/**
 * IaKlaviyoCart — adds products (and a discount code) to the cart from URL parameters, then cleans the URL.
 *
 * Promo flow: a Klaviyo email / pop-up links to any storefront path with our custom parameters:
 *
 *   https://shop.com/collections/best-sellers?ia_add=40123:1,40456:2&ia_discount=CODE&utm_campaign=…&_kx=…
 *
 *   - ia_add:      the variants to pre-load ("variant:qty", comma separated).
 *   - ia_discount: optional discount code(s), comma separated. Applied to the cart in the background
 *                  (Cart API /cart/update.js), so the products, the code and Klaviyo's own tracking params
 *                  all survive. (Shopify's /discount/CODE?redirect= link drops the redirect's query string
 *                  as soon as Klaviyo adds its utm/_kx params, which emptied the cart.)
 *   - discount:    accepted too, for links built by hand.
 *
 * On load it parses the params, removes them from the URL (a refresh/back/share never re-adds), adds the
 * variants that aren't already in the cart in that quantity (a second click doesn't double the samples),
 * applies the code(s) on top of any code already on the cart, refreshes the cart UI (mirroring
 * product-form.js) and opens the cart drawer. Every other parameter (utm, _kx, …) stays in the URL.
 *
 * Rendered once near </body> in layout/theme.liquid. Param names stay in sync with the IA Klaviyo Link
 * Builder section.
 */
class IaKlaviyoCart extends HTMLElement {
  connectedCallback() {
    this.param = this.dataset.param || 'ia_add';
    this.discountParams = (this.dataset.discountParam || 'ia_discount,discount').split(',').map((p) => p.trim()).filter(Boolean);

    const url = new URL(window.location.href);
    const raw = url.searchParams.get(this.param);
    const codes = this.parseCodes(this.discountParams.map((p) => url.searchParams.get(p)).filter(Boolean).join(','));
    if (!raw && !codes.length) return;

    const items = raw ? this.parseItems(raw) : [];

    // Always strip our params first so a refresh/back/share can't re-add.
    url.searchParams.delete(this.param);
    this.discountParams.forEach((p) => url.searchParams.delete(p));
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);

    if (items.length || codes.length) this.runWhenReady(items, codes);
  }

  /**
   * The cart drawer/notification script is deferred and may run after this one,
   * so the element exists in the DOM but isn't upgraded yet. Wait until its
   * custom element is defined before calling its methods.
   */
  runWhenReady(items, codes) {
    const tag = document.querySelector('cart-notification')
      ? 'cart-notification'
      : document.querySelector('cart-drawer')
      ? 'cart-drawer'
      : null;

    if (!tag) {
      this.apply(items, codes, null);
      return;
    }

    customElements.whenDefined(tag).then(() => this.apply(items, codes, document.querySelector(tag)));
  }

  /**
   * "40123:1,40456:2" -> [{ id: '40123', quantity: 1 }, { id: '40456', quantity: 2 }].
   * The id is the last run of digits in each entry, so a pasted variant URL or
   * gid://shopify/ProductVariant/123 also resolves. Quantity defaults to 1.
   */
  parseItems(raw) {
    const merged = new Map();
    raw
      .split(',')
      .map((entry) => {
        const [idPart, qtyPart] = entry.split(':');
        const ids = String(idPart).match(/\d+/g);
        if (!ids) return null;
        return { id: ids[ids.length - 1], quantity: Math.max(1, parseInt(qtyPart, 10) || 1) };
      })
      .filter(Boolean)
      .forEach((item) => merged.set(item.id, (merged.get(item.id) || 0) + item.quantity));
    return [...merged].map(([id, quantity]) => ({ id, quantity }));
  }

  // "CODE1, code2" -> ['CODE1', 'code2'] (trimmed, empties and repeats dropped)
  parseCodes(raw) {
    const seen = new Set();
    return String(raw || '')
      .split(',')
      .map((code) => code.trim())
      .filter((code) => code && !seen.has(code.toLowerCase()) && seen.add(code.toLowerCase()));
  }

  async request(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  }

  async apply(items, codes, cart) {
    const sections = cart ? cart.getSectionsToRender().map((section) => section.section) : ['cart-icon-bubble'];
    const render = { sections: sections.join(','), sections_url: window.location.pathname };

    try {
      const current = await (await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();

      // Only add what isn't in the cart yet, so clicking the same link twice doesn't add a second sample.
      const inCart = new Map();
      (current.items || []).forEach((line) => inCart.set(String(line.variant_id), (inCart.get(String(line.variant_id)) || 0) + line.quantity));
      const toAdd = items
        .map((item) => ({ id: item.id, quantity: item.quantity - (inCart.get(String(item.id)) || 0) }))
        .filter((item) => item.quantity > 0);

      if (!toAdd.length && !codes.length) return;

      if (toAdd.length) {
        const added = await this.request(window.routes.cart_add_url, { items: toAdd });
        if (added.status) {
          // Sold out / limited stock / not on the Online Store / unknown variant: Shopify may still have added part of
          // it ("Only 1 item was added…"). Report it and carry on, so the landing (and the code) still work.
          console.warn('[ia-klaviyo-cart] add to cart failed:', added.description || added.message);
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'ia-klaviyo-cart',
              errors: added.errors || added.description,
              message: added.message,
            });
          }
        }
      }

      // One cart update returns the whole cart (count, lines, discounts) + the drawer/header markup to render.
      const update = { ...render };
      if (codes.length) {
        // Keep any code already on the cart; codes only stack if the discounts are set to combine in Shopify.
        const existing = (current.discount_codes || []).map((discount) => discount.code);
        update.discount = this.parseCodes([...existing, ...codes].join(',')).join(',');
      } else {
        update.updates = {};
      }
      const state = await this.request(`${window.routes.cart_update_url}.js`, update);
      if (state.status) {
        console.warn('[ia-klaviyo-cart] cart update failed:', state.description || state.message);
        return;
      }
      if (!codes.length && state.item_count === current.item_count) return;

      // A code-only link (or products that couldn't be added) applies the code quietly: no empty drawer.
      if (cart && state.item_count > 0) {
        cart.classList.remove('is-empty');
        cart.renderContents(state);
      } else if (!cart) {
        this.updateCartBubble(state);
      }

      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ia-klaviyo-cart', cartData: state });
      }
    } catch (error) {
      console.error('[ia-klaviyo-cart]', error);
    }
  }

  // Fallback for themes without a cart drawer/notification: refresh the header count.
  updateCartBubble(data) {
    const markup = data.sections && data.sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (!markup || !bubble) return;
    const inner = new DOMParser().parseFromString(markup, 'text/html').querySelector('.shopify-section');
    bubble.innerHTML = inner ? inner.innerHTML : markup;
  }
}

if (!customElements.get('ia-klaviyo-cart')) {
  customElements.define('ia-klaviyo-cart', IaKlaviyoCart);
}
